import React, { useRef, useCallback, useEffect, useState, forwardRef, useMemo } from 'react';
import { Canvas as CanvasType } from 'fabric';
import { WhiteboardCanvas } from '../WhiteboardCanvas';
import ModernToolbar from '../ModernToolbar';
import MobileToolbar from '../MobileToolbar';
import { useNavigate } from 'react-router-dom';

import { OpticalAnswerManager } from './features/OpticalAnswerManager';
import { OpticalAnswerPanel } from './features/OpticalAnswerPanel';
import { OpticalPanelToggleButton } from './features/OpticalPanelToggleButton';
import { WhiteboardProvider, useWhiteboard } from './providers/WhiteboardProvider';
import { useWhiteboardStore } from '../../store/useWhiteboardStore';
import { useTestStore } from '../../store/useTestStore';
import { Tool } from '../../types';
import { WhiteboardExternalProps } from '../../types/whiteboardProps';
import { getStandardQuestionId } from '../../utils/questionIdUtils';
import { detectDevice } from '../../utils/deviceDetection';

// Additional UI components
import DraggableQuestionNav from '../DraggableQuestionNav';
import MobileQuestionNav from '../MobileQuestionNav';
import TabletQuestionNav from '../TabletQuestionNav';
import EliminatedChoicesDisplay from '../EliminatedChoicesDisplay';
import VideoModal from '../VideoModal';
import SolutionSelector from '../SolutionSelector';
import { LoadingStates } from '../LoadingStates';
import ErrorBoundary from '../ErrorBoundary';
import { RouteGuard } from '../RouteGuard';
import { FaArrowLeft } from 'react-icons/fa';
import ExitConfirmModal from '../ExitConfirmModal';
import { useWebView } from '../../integrations/webview';
import { WebViewMessageService } from '../../integrations/webview/services/messageService';
import { WebViewMessageType } from '../../integrations/webview/types';
import { TestTimer, MobileTestTimer } from '../TestTimer';
import QuestionTimer, { MobileQuestionTimer } from '../QuestionTimer';
import { OpticalAnswerBulkSubmit } from '../OpticalAnswerBulkSubmit';
import { TouchDebugOverlay } from '../TouchDebugOverlay';
import { IntervalTrackingDebug, IntervalStatusIndicator } from '../IntervalTrackingDebug';
import { cleanupTestData, cleanupWebViewTestData } from '../../utils/testCleanup';
import { WebViewExitHandler, ExitState } from '../WebViewExitHandler';
import { WebViewRecoveryService, saveAnswersForRecovery, checkAndRecoverSession } from '../../utils/webViewRecovery';

interface WhiteboardContainerProps {
  externalProps?: WhiteboardExternalProps;
  isMobile?: boolean;
  isPlayback?: boolean;
  showToolbar?: boolean;
}

// Inner component that uses the context
const WhiteboardContent: React.FC<{
  isMobile: boolean;
  isPlayback: boolean;
  showToolbar: boolean;
  externalProps?: WhiteboardExternalProps;
}> = ({ isMobile, isPlayback, showToolbar, externalProps }) => {
  const whiteboardCanvasRef = useRef<any>(null);
  const navigate = useNavigate();
  
  // Local state for modals and UI elements
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string>('');
  const [isSolutionSelectorOpen, setIsSolutionSelectorOpen] = useState(false);
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const [currentSolutions, setCurrentSolutions] = useState<string[]>([]);
  // Get elimination display state from store
  const { showEliminationDisplay, setShowEliminationDisplay } = useWhiteboardStore();
  const previousQuestionIdRef = useRef<string | undefined>(undefined);
  const questionStartTimeRef = useRef<number | null>(null);
  const currentQuestionIdRef = useRef<string | null>(null);
  
  // Timer state
  const [remainingTime, setRemainingTime] = useState<number | undefined>(undefined);
  const [isTimerPaused, setIsTimerPaused] = useState(false);
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());
  
  // Tablet-specific states
  const [showTabletQuestionNav, setShowTabletQuestionNav] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState(() => detectDevice());
  const isTablet = deviceInfo.type === 'tablet';
  
  // WebView exit state management
  const [exitState, setExitState] = useState<ExitState>(ExitState.NONE);
  const [exitRetryCount, setExitRetryCount] = useState(0);
  const [pendingAnswerData, setPendingAnswerData] = useState<unknown>(null);
  const exitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const exitCleanupRef = useRef<(() => void) | null>(null);
  const exitAbortControllerRef = useRef<AbortController | null>(null);
  
  
  // Update device info on resize
  useEffect(() => {
    const handleResize = () => {
      setDeviceInfo(detectDevice());
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);
  
  // Get data from provider FIRST - before using qbTestId
  const {
    qbQuestions,
    qbGroups,
    currentQuestionIndex,
    currentQbQuestion,
    navigateToQuestion,
    goToNextQuestion,
    goToPreviousQuestion,
    fabricCanvasRef,
    canvasReady,
    setCanvasReady,
    opticalAnswers,
    eliminatedChoices,
    submitOpticalAnswer,
    eliminateChoice,
    showOpticalPanelState,
    setShowOpticalPanelState,
    isWebViewMode,
    isOpticalMode,
    saveQuestionData,
    loadQuestionData,
    savePendingOpticalAnswers,
    isRecording,
    qbTestId,
    isLoadingTest,
    isLoadingQuestions,
    error,
    webViewLoadingError,
    testTitle,
    totalTime,
    sessionCount,
    previousSessionsTime,
    isTestCompleted,
    handleTestComplete
  } = useWhiteboard();

  // WebView test change cleanup - proactive cleanup when qbTestId changes
  useEffect(() => {
    if (qbTestId && externalProps?.webViewMode) {
      // Only log on test change, don't cleanup on initial load
      console.log('📝 WebView test changed to:', qbTestId);
    }
  }, [qbTestId, externalProps?.webViewMode]);
  
  // Store hooks
  const { 
    currentTool, 
    currentColor, 
    currentWidth
  } = useWhiteboardStore();
  
  // WebView hook
  const { isWebView } = useWebView();
  
  // Debug optical answers
  useEffect(() => {    
  }, [opticalAnswers, isOpticalMode, isMobile]);
  
  // Check for recovery data on mount (WebView mode only)
  useEffect(() => {
    if (!externalProps?.webViewMode) return;
    
    const checkRecovery = async () => {
      const recoveryData = await checkAndRecoverSession();
      if (recoveryData) {
        // Found recovery data, attempting to restore session
        setPendingAnswerData(recoveryData.answers);
        
        // Try to send the recovered data
        const recoveryService = WebViewRecoveryService.getInstance();
        const success = await recoveryService.attemptRecovery();
        
        if (success) {
          setExitState(ExitState.COMPLETED);
          setTimeout(() => window.close(), 2000);
        } else {
          setExitState(ExitState.FAILED);
        }
      }
    };
    
    checkRecovery();
  }, [externalProps?.webViewMode]);
  
  // Determine test mode and timing from externalProps
  const testMode = useMemo(() => {
    return externalProps?.studyTask?.mode || externalProps?.testConfig?.studyTask?.mode || 'UnlimitedTest';
  }, [externalProps]);
  
  const testParams = useMemo(() => {
    return externalProps?.studyTask?.params || externalProps?.testConfig?.studyTask?.params || {};
  }, [externalProps]);
  
  // Calculate timer configuration
  const timerConfig = useMemo(() => {
    const isTimeTest = testMode === 'TimeTest';
    const isTimedQuestion = testMode === 'TimedQuestion';
    
    // For TimeTest mode - total test time
    const totalTimeInSeconds = isTimeTest ? (testParams.totalTime || totalTime || 0) : 0;
    
    // For TimedQuestion mode - time per question
    const timePerQuestion = isTimedQuestion ? (testParams.timePerQuestion || testParams.questionTimeLimit || 60) : 0;
    
    return {
      isTimeTest,
      isTimedQuestion,
      totalTimeInSeconds,
      timePerQuestion,
      showTestTimer: isTimeTest && totalTimeInSeconds > 0,
      showQuestionTimer: isTimedQuestion && timePerQuestion > 0,
      allowBackNavigation: testParams.allowBackNavigation !== false, // Default true unless explicitly false
      showQuestionTimerUI: testParams.showQuestionTimer !== false // Default true unless explicitly false
    };
  }, [testMode, testParams, totalTime]);
  
  // Handle video click
  const handleVideoClick = useCallback(() => {
    if (currentQbQuestion?.href) {
      // Handle both string and array formats
      let videoUrl: string | undefined;
      const href = currentQbQuestion.href as string | Array<{ videoUrl?: string; audioUrl?: string; pdfUrl?: string; title?: string }>;
      
      if (typeof href === 'string') {
        videoUrl = href;
      } else if (Array.isArray(href) && href.length > 0) {
        // If it's an array, find the first video URL
        const firstVideo = href.find(item => item.videoUrl);
        videoUrl = firstVideo?.videoUrl;
      }
      
      if (videoUrl) {
        setCurrentVideoUrl(videoUrl);
        setIsVideoModalOpen(true);
      } else {
        console.warn('No valid video URL found in question href:', href);
      }
    }
  }, [currentQbQuestion]);
  
  // Handle canvas initialization
  const handleCanvasInit = useCallback((canvas: CanvasType) => {
  
    fabricCanvasRef.current = canvas;
    setCanvasReady(true);
  }, [fabricCanvasRef, setCanvasReady]);
  
  // Handle back button click
  const handleBackClick = useCallback(async () => {
    if (isWebView) {
      // In WebView mode, show exit confirmation modal
      setIsExitModalOpen(true);
    } else {
      // In normal mode, save data first
      await savePendingOpticalAnswers();
      
      // Navigate first, then clean up to avoid showing error
      const navigateAway = () => {
        // Check if running in native WebView (iOS/Android)
        if ((window as any).webkit?.messageHandlers?.goBack) {
          // iOS WebView
          (window as any).webkit.messageHandlers.goBack.postMessage({});
        } else if ((window as any).Android?.goBack) {
          // Android WebView
          (window as any).Android.goBack();
        } else if (window.history.length > 1) {
          // Regular web browser
          window.history.back();
        } else {
          // Fallback to library
          navigate('/library');
        }
      };
      
      navigateAway();
      
      // Clean up after navigation starts
      setTimeout(async () => {
        if (qbTestId) {
          await cleanupTestData(qbTestId);
        }
      }, 100);
    }
  }, [isWebView, navigate, savePendingOpticalAnswers, qbTestId]);
  
  // Collect answer data for WebView messages
  const collectAnswerData = useCallback(() => {
    const testStore = useTestStore.getState();
    const whiteboardStore = useWhiteboardStore.getState();
    
    // First, end the current trajectory session if active
    if (currentQbQuestion && questionStartTimeRef.current) {
      const { getStandardQuestionId } = require('../../utils/questionIdUtils');
      const currentQuestionId = getStandardQuestionId(currentQbQuestion);
      
      if (currentQuestionId && currentQuestionIdRef.current === currentQuestionId) {
        const endTime = Date.now();
        const duration = (endTime - questionStartTimeRef.current) / 1000;
        
        // Update the current question's trajectory in cache
        const currentData = whiteboardStore.questionDataCache.get(currentQuestionId);
        if (currentData && currentData.trajectory && currentData.trajectory.length > 0) {
          const lastSession = currentData.trajectory[currentData.trajectory.length - 1];
          if (!lastSession.endDate) {
            lastSession.endDate = endTime;
            lastSession.duration = duration;
            console.log('📝 Ended active trajectory session for data collection:', currentQuestionId);
          }
        }
      }
    }
    
    // Collect answers in the final format
    const answers: any[] = [];
    
    Array.from(whiteboardStore.questionDataCache.entries()).forEach(([questionId, data]) => {
      // Skip if no answer selected
      if (!data.opticalChoice) {
        return;
      }
      
      // Calculate intervals and total duration
      let intervals: string = '';
      let totalDuration = 0;
      
      if (data.trajectory && data.trajectory.length > 0) {
        const formattedIntervals = data.trajectory.map((session) => {
          let duration = session.duration;
          let endTime = session.endDate;
          
          // For active sessions, calculate current duration
          if (!session.endDate && !session.duration) {
            const currentTime = Date.now();
            duration = (currentTime - session.startDate) / 1000;
            endTime = currentTime;
          } else if (!duration && session.endDate) {
            duration = (session.endDate - session.startDate) / 1000;
          }
          
          totalDuration += duration || 0;
          
          return {
            start: new Date(session.startDate).toISOString().replace('T', ' ').substring(0, 19),
            end: endTime ? new Date(endTime).toISOString().replace('T', ' ').substring(0, 19) : null,
            duration: Math.round((duration || 0) * 100) / 100
          };
        });
        
        intervals = JSON.stringify(formattedIntervals);
      }
      
      answers.push({
        questionId,
        studentAnswer: data.opticalChoice || '0',
        trajectory: data.selectionHistory || '',
        eliminationTrajectory: data.eliminatedChoices || '',
        intervals: intervals,
        totalDuration: Math.round(totalDuration * 100) / 100,
        isSkipped: false,
        isEvaluate: true,
        processed: false,
        proficiencyInOutcome: 0
      });
    });
    
    return {
      // Basic info
      studyTaskId: externalProps?.studyTask?.id,
      userAccountId: externalProps?.authConfig?.userInfo?.id,
      testId: qbTestId || testStore.currentTestId,
      
      // Answers in final format
      answers: answers,
      
      // Optical answers (current selections) - for backward compatibility
      opticalAnswers: testStore.opticalAnswers,
      
      // Eliminated choices - for backward compatibility
      eliminatedChoices: testStore.eliminatedChoices,
      
      // Question data with history - for backward compatibility
      questionData: Array.from(whiteboardStore.questionDataCache.entries()).map(([questionId, data]) => ({
        questionId,
        opticalChoice: data.opticalChoice,
        selectionHistory: data.selectionHistory,
        eliminatedChoices: data.eliminatedChoices,
        totalTime: data.totalTime,
        trajectory: data.trajectory,
        hasDrawings: !!(data.drawingData?.strokes?.length || data.drawingData?.fabricObjects?.length),
        // Add intervals field - format trajectory data as intervals JSON string
        intervals: data.trajectory && data.trajectory.length > 0 
          ? JSON.stringify(data.trajectory.map((session) => {
              const endTime = session.endDate || Date.now();
              
              return {
                start: new Date(session.startDate).toISOString(),
                end: new Date(endTime).toISOString()
              };
            }))
          : null
      })),
      
      // Test metadata
      totalQuestions: qbQuestions.length,
      answeredQuestions: Object.keys(testStore.opticalAnswers).length,
      currentQuestionIndex,
      testTitle: testStore.testTitle || testTitle,
      
      // Session info
      sessionStartTime: testStore.sessionStartTime,
      sessionCount: testStore.sessionCount,
      isTestCompleted: testStore.isTestCompleted,
      
      // Additional metadata
      timestamp: Date.now()
    };
  }, [externalProps, qbTestId, qbQuestions.length, currentQuestionIndex, testTitle, currentQbQuestion]);
  
  // Handle exit confirmation
  const handleExitConfirm = useCallback(async () => {
    // Create new AbortController for this exit attempt
    exitAbortControllerRef.current = new AbortController();
    const abortSignal = exitAbortControllerRef.current.signal;
    
    try {
      // Clean up any existing timeout/listener
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
        exitTimeoutRef.current = null;
      }
      if (exitCleanupRef.current) {
        exitCleanupRef.current();
        exitCleanupRef.current = null;
      }
      
      // Check if aborted
      if (abortSignal.aborted) {
        return;
      }
      
      // Set exit state to pending
      if (externalProps?.webViewMode) {
        setExitState(ExitState.PENDING);
      }
      
      // First save any pending optical answers
      await savePendingOpticalAnswers();
      
      // Check if aborted after async operation
      if (abortSignal.aborted) {
        setExitState(ExitState.NONE);
        return;
      }
      
      // Collect answer data and send exit message
      const answerData = collectAnswerData();
      setPendingAnswerData(answerData);
      
      // Save for recovery in case of failure
      if (externalProps?.webViewMode && qbTestId) {
        saveAnswersForRecovery(qbTestId, currentQuestionIndex, answerData);
      }
      
      const messageService = WebViewMessageService.getInstance();
      
      // Try to send exit message
      const sendExitMessage = () => new Promise((resolve, reject) => {
        // Check if already aborted
        if (abortSignal.aborted) {
          reject(new Error('Operation cancelled'));
          return;
        }
        
        try {
          messageService.send(WebViewMessageType.EXIT_REQUESTED, answerData);
          
          // Wait for response with timeout (30 seconds for user to decide in app modal)
          exitTimeoutRef.current = setTimeout(() => {
            if (!abortSignal.aborted) {
              exitTimeoutRef.current = null;
              exitCleanupRef.current = null;
              reject(new Error('Exit request timeout'));
            }
          }, 30000); // 30 seconds timeout to allow user decision in app
          
          // Listen for confirmation
          exitCleanupRef.current = messageService.addHandler(WebViewMessageType.TEST_COMPLETED, () => {
            if (!abortSignal.aborted) {
              if (exitTimeoutRef.current) {
                clearTimeout(exitTimeoutRef.current);
                exitTimeoutRef.current = null;
              }
              if (exitCleanupRef.current) {
                exitCleanupRef.current();
                exitCleanupRef.current = null;
              }
              resolve(true);
            }
          });
          
          // Listen for abort signal
          abortSignal.addEventListener('abort', () => {
            if (exitTimeoutRef.current) {
              clearTimeout(exitTimeoutRef.current);
              exitTimeoutRef.current = null;
            }
            if (exitCleanupRef.current) {
              exitCleanupRef.current();
              exitCleanupRef.current = null;
            }
            reject(new Error('Operation cancelled'));
          });
          
        } catch (error) {
          reject(error);
        }
      });
      
      await sendExitMessage();
      
      if (externalProps?.webViewMode) {
        // Clean up data immediately and then show completion state
        if (qbTestId) {
          await cleanupWebViewTestData(qbTestId);
        }
        
        setExitState(ExitState.COMPLETED);
        
        // Minimal delay just for UI feedback, then close
        setTimeout(() => {
          window.close();
        }, 300);
      } else {
        // Clean up immediately in normal mode
        if (qbTestId) {
          await cleanupWebViewTestData(qbTestId);
        }
      }
      
      // Close modal
      setIsExitModalOpen(false);
      
    } catch (error: any) {
      // Don't show error if operation was cancelled
      if (error?.message === 'Operation cancelled' || exitAbortControllerRef.current?.signal.aborted) {
        if (externalProps?.webViewMode) {
          setExitState(ExitState.NONE);
        }
        return;
      }
      
      // Exit failed
      if (externalProps?.webViewMode) {
        setExitState(ExitState.FAILED);
        setExitRetryCount(prev => prev + 1);
      }
      
      // Keep modal open for retry in normal mode
      if (!externalProps?.webViewMode) {
        alert('Çıkış işlemi başarısız oldu. Lütfen tekrar deneyin.');
      }
    } finally {
      // Clean up AbortController
      exitAbortControllerRef.current = null;
    }
  }, [collectAnswerData, savePendingOpticalAnswers, qbTestId, externalProps?.webViewMode]);
  
  // Handle retry for WebView exit
  const handleExitRetry = useCallback(async () => {
    if (!pendingAnswerData) return;
    
    try {
      setExitState(ExitState.PENDING);
      
      const messageService = WebViewMessageService.getInstance();
      
      // Try to send exit message again
      const sendExitMessage = () => new Promise((resolve, reject) => {
        try {
          messageService.send(WebViewMessageType.EXIT_REQUESTED, pendingAnswerData);
          
          const timeout = setTimeout(() => {
            reject(new Error('Exit request timeout'));
          }, 30000); // 30 seconds timeout for retry as well
          
          const cleanup = messageService.addHandler(WebViewMessageType.TEST_COMPLETED, () => {
            clearTimeout(timeout);
            cleanup();
            resolve(true);
          });
          
        } catch (error) {
          reject(error);
        }
      });
      
      await sendExitMessage();
      
      // Success - cleanup immediately
      if (qbTestId) {
        await cleanupWebViewTestData(qbTestId);
      }
      setExitState(ExitState.COMPLETED);
      
      setTimeout(() => {
        window.close();
      }, 500);
      
    } catch (error) {
      // Retry failed
      setExitState(ExitState.FAILED);
      setExitRetryCount(prev => prev + 1);
    }
  }, [pendingAnswerData, qbTestId]);
  
  // Handle test timer time up (TimeTest mode)
  const handleTestTimeUp = useCallback(async () => {
    console.log('⏰ Test time is up! Completing test...');
    await handleTestComplete();
  }, [handleTestComplete]);
  
  // Handle question timer time up (TimedQuestion mode)
  const handleQuestionTimeUp = useCallback(async () => {
    console.log('⏰ Question time is up! Moving to next question...');
    
    // Save current question data
    await saveQuestionData();
    
    // If this is the last question, complete the test
    if (currentQuestionIndex >= qbQuestions.length - 1) {
      await handleTestComplete();
    } else {
      // Move to next question
      await goToNextQuestion();
      setQuestionStartTime(Date.now());
    }
  }, [currentQuestionIndex, qbQuestions.length, saveQuestionData, goToNextQuestion, handleTestComplete]);
  
  
  // Save pending answers on unmount
  useEffect(() => {
    return () => {
      // Clean up any pending exit operations
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
        exitTimeoutRef.current = null;
      }
      if (exitCleanupRef.current) {
        exitCleanupRef.current();
        exitCleanupRef.current = null;
      }
      
      // Save any pending optical answers when component unmounts
      savePendingOpticalAnswers().catch(error => {
        console.error('Error saving optical answers on unmount:', error);
      });
    };
  }, [savePendingOpticalAnswers]);
  
  // Monitor WebView connection
  useEffect(() => {
    if (!externalProps?.webViewMode) return;
    
    let heartbeatInterval: NodeJS.Timeout;
    let lastHeartbeat = Date.now();
    
    const checkConnection = () => {
      const now = Date.now();
      // If no communication for 10 seconds, consider connection lost
      if (now - lastHeartbeat > 10000) {
        setExitState(ExitState.CONNECTION_LOST);
        
        // Start auto-recovery when connection is lost
        const recoveryService = WebViewRecoveryService.getInstance();
        recoveryService.startAutoRecovery(
          () => {
            // On success
            setExitState(ExitState.COMPLETED);
            setTimeout(() => window.close(), 2000);
          },
          () => {
            // On fail - keep trying
            // Recovery attempt failed, will retry
          }
        );
      }
    };
    
    // Set up heartbeat monitoring
    heartbeatInterval = setInterval(checkConnection, 5000);
    
    // Listen for any message to update heartbeat
    const messageService = WebViewMessageService.getInstance();
    const updateHeartbeat = () => {
      lastHeartbeat = Date.now();
      if (exitState === ExitState.CONNECTION_LOST) {
        setExitState(ExitState.NONE);
        // Stop auto-recovery if connection is restored
        const recoveryService = WebViewRecoveryService.getInstance();
        recoveryService.stopAutoRecovery();
      }
    };
    
    // Add generic message listener for heartbeat
    window.addEventListener('message', updateHeartbeat);
    
    return () => {
      clearInterval(heartbeatInterval);
      window.removeEventListener('message', updateHeartbeat);
    };
  }, [externalProps?.webViewMode, exitState]);
  
  // Handle question change
  const handleQuestionChange = useCallback(async (index: number) => {
    // In TimedQuestion mode, check if back navigation is allowed
    if (timerConfig.isTimedQuestion && !timerConfig.allowBackNavigation && index < currentQuestionIndex) {
      console.warn('Back navigation not allowed in TimedQuestion mode');
      return;
    }
    
    // Save current question data before changing
    await saveQuestionData();
    
    // Navigate to new question
    await navigateToQuestion(index);
    
    // Reset question timer for TimedQuestion mode
    if (timerConfig.isTimedQuestion) {
      setQuestionStartTime(Date.now());
    }
  }, [currentQuestionIndex, saveQuestionData, navigateToQuestion, timerConfig]);
  
  // Load question data when question changes
  useEffect(() => {  
    const loadDataForQuestion = async () => {
      if (!currentQbQuestion) return;
      
      // Get standardized question ID
      const { getStandardQuestionId } = await import('../../utils/questionIdUtils');
      const standardQuestionId = getStandardQuestionId(currentQbQuestion);
      
      // Only load if question ID actually changed
      if (standardQuestionId && previousQuestionIdRef.current !== standardQuestionId) {
       
        
        previousQuestionIdRef.current = standardQuestionId;
        
        // Update currentQuestionId in whiteboard store
        useWhiteboardStore.getState().setCurrentQuestionId(standardQuestionId);
       
        
        // For first question, load immediately without waiting for canvas
        if (currentQuestionIndex === 0 && !canvasReady) {
          console.log('📝 Loading first question data before canvas is ready');
          await loadQuestionData();
        } else if (canvasReady) {
          // Small delay to ensure canvas is stable
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Load the question data
          await loadQuestionData();
        }
        
      }
    };
    
    loadDataForQuestion();
  }, [currentQbQuestion, canvasReady, currentQuestionIndex, loadQuestionData]);
  
  // Handle optical answer selection
  const handleOpticalAnswerSelect = useCallback((choice: 'A' | 'B' | 'C' | 'D' | 'E' | '') => {
    submitOpticalAnswer(choice);
  }, [submitOpticalAnswer]);
  
  // Handle optical choice elimination
  const handleOpticalChoiceEliminate = useCallback((choice: string) => {
    eliminateChoice(choice);
  }, [eliminateChoice]);
  
  // Handle elimination display toggle
  const handleToggleEliminationDisplay = useCallback(() => {
    setShowEliminationDisplay(!showEliminationDisplay);
  }, [showEliminationDisplay, setShowEliminationDisplay]);
  
  // Memoize eliminatedChoices transformation to prevent unnecessary re-renders
  const eliminatedChoicesForCanvas = useMemo(() => {
    // Only transform the current question's eliminated choices
    if (!currentQbQuestion) return {};
    
    const standardQuestionId = getStandardQuestionId(currentQbQuestion);
    if (!standardQuestionId) return {};
    
    const currentChoices = eliminatedChoices[standardQuestionId];
    if (!currentChoices || currentChoices.size === 0) return {};
    
    return {
      [standardQuestionId]: Array.from(currentChoices).join(',')
    };
  }, [eliminatedChoices, currentQbQuestion]);
  
  // Memoize eliminatedChoices string format for display
  const eliminatedChoicesString = useMemo(() => {
    const result: Record<string, string> = {};
    Object.entries(eliminatedChoices).forEach(([key, value]) => {
      result[key] = Array.from(value).join('');
    });
    return result;
  }, [eliminatedChoices]);
  
  // Loading state
  if (isLoadingTest || isLoadingQuestions) {
    return <LoadingStates type="test" />;
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
          <h2 className="text-xl font-semibold text-red-600 mb-4">Test Yükleme Hatası</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/library')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <FaArrowLeft className="text-sm" />
            Test Listesine Dön
          </button>
        </div>
      </div>
    );
  }

  // Show WebView exit handler if in exit state or loading error
  // This check must come BEFORE the "no questions" check to prevent showing error after cleanup
  if (externalProps?.webViewMode && (exitState !== ExitState.NONE || webViewLoadingError)) {
    return (
      <WebViewExitHandler
        exitState={webViewLoadingError ? ExitState.LOADING_ERROR : exitState}
        onRetry={handleExitRetry}
        retryCount={exitRetryCount}
        maxRetries={3}
        answerData={pendingAnswerData}
      />
    );
  }

  // No questions - but only show error if not in WebView mode with exit state
  if (qbQuestions.length === 0) {
    // In WebView mode, if we have no questions during exit, just return null
    // This prevents the error screen from showing after cleanup
    if (externalProps?.webViewMode) {
      return null;
    }
    
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
          <h2 className="text-xl font-semibold mb-4">Soru Bulunamadı</h2>
          <p className="text-gray-600 mb-2">Test ID: {qbTestId} için soru bulunamadı</p>
          <p className="text-sm text-gray-500 mb-6">
            Test ID'sinin doğru olduğundan veya bu teste erişim izninizin olduğundan emin olun.
          </p>
          <button
            onClick={() => navigate('/library')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <FaArrowLeft className="text-sm" />
            Test Listesine Dön
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="whiteboard-container flex flex-col h-full w-full relative">
      {/* Touch Debug Overlay - only show in development on mobile */}
      {import.meta.env.DEV && isMobile && <TouchDebugOverlay />}
      {/* Back Button - Rectangle design */}
      <button
        onClick={handleBackClick}
        className="absolute top-0 left-0 z-[100] h-[25px] px-3 bg-gray-100 hover:bg-gray-200 transition-all duration-200 group rounded-br-md border-b border-r border-gray-300 flex items-center gap-1.5"
        title="Çıkış"
      >
        <FaArrowLeft className="w-3 h-3 text-gray-600 group-hover:text-gray-800 transition-colors" />
        <span className="text-xs font-medium text-gray-600 group-hover:text-gray-800">Çıkış</span>
      </button>
      
      {/* Mobile Timer Container - Both timers in one container for mobile */}
      {isMobile && (timerConfig.showTestTimer || (timerConfig.showQuestionTimer && timerConfig.showQuestionTimerUI)) && !isTestCompleted && (
        <div className="absolute top-0 right-0 z-[100] h-[25px] flex items-center gap-2 bg-gray-100 rounded-bl-md border-b border-l border-gray-300 px-2">
          {timerConfig.showTestTimer && (
            <MobileTestTimer
              totalSeconds={timerConfig.totalTimeInSeconds}
              onTimeUp={handleTestTimeUp}
              isPaused={isTimerPaused}
              sessionInfo={{
                sessionCount,
                previousSessionsTime,
                currentSessionStart: Date.now() - (previousSessionsTime * 1000)
              }}
            />
          )}
          {timerConfig.showQuestionTimer && timerConfig.showQuestionTimerUI && (
            <MobileQuestionTimer
              timeLimit={timerConfig.timePerQuestion}
              onTimeUp={handleQuestionTimeUp}
              show={true}
              questionId={currentQbQuestion?.questionId}
              isPaused={isTimerPaused}
            />
          )}
        </div>
      )}

      {/* Desktop Test Timer - TimeTest mode */}
      {!isMobile && timerConfig.showTestTimer && !isTestCompleted && (
        <div className="absolute top-4 right-4 z-[100]">
          <TestTimer
            totalSeconds={timerConfig.totalTimeInSeconds}
            onTimeUp={handleTestTimeUp}
            isPaused={isTimerPaused}
            sessionInfo={{
              sessionCount,
              previousSessionsTime,
              currentSessionStart: Date.now() - (previousSessionsTime * 1000)
            }}
          />
        </div>
      )}
      
      {/* Desktop Question Timer - TimedQuestion mode */}
      {!isMobile && timerConfig.showQuestionTimer && timerConfig.showQuestionTimerUI && !isTestCompleted && (
        <QuestionTimer
          timeLimit={timerConfig.timePerQuestion}
          onTimeUp={handleQuestionTimeUp}
          show={true}
          position="top-right"
          questionId={currentQbQuestion?.questionId}
          isPaused={isTimerPaused}
        />
      )}
      
      {/* Mobile Question Navigation - Show on mobile and tablets when tablet nav is hidden */}
      {(isMobile || (isTablet && !showTabletQuestionNav)) && qbQuestions.length > 0 && !isPlayback && (
        <MobileQuestionNav
          questions={qbQuestions}
          currentIndex={currentQuestionIndex}
          onQuestionSelect={navigateToQuestion}
          onNext={goToNextQuestion}
          onPrevious={goToPreviousQuestion}
          hasNext={currentQuestionIndex < qbQuestions.length - 1}
          hasPrevious={currentQuestionIndex > 0}
        />
      )}
      
      {/* Toolbar */}
      {showToolbar && !isPlayback && (
        <>
          {isMobile ? (
            <MobileToolbar
              questions={qbQuestions}
              qbGroups={qbGroups}
              currentIndex={currentQuestionIndex}
              currentQbQuestion={currentQbQuestion}
              showOpticalForm={isOpticalMode}
              opticalAnswers={opticalAnswers}
              onAnswerSelect={(answer: 'A' | 'B' | 'C' | 'D' | 'E', questionId?: string) => {
                submitOpticalAnswer(answer);
              }}
              hasVideo={(() => {
                if (!currentQbQuestion?.href) return false;
                const href = currentQbQuestion.href as string | Array<{ videoUrl?: string; audioUrl?: string; pdfUrl?: string; title?: string }>;
                if (typeof href === 'string') return true;
                if (Array.isArray(href)) {
                  return href.some(item => !!item.videoUrl);
                }
                return false;
              })()}
              onVideoClick={handleVideoClick}
              onNavigatePrevious={goToPreviousQuestion}
              onNavigateNext={goToNextQuestion}
              onQuestionChange={navigateToQuestion}
              allowExport={externalProps?.uiConfig?.allowExport ?? false}
              // Recording permissions
              recordingPermissions={externalProps?.uiConfig?.recordingPermissions || {
                canRecord: true,
                canViewRecordings: true,
                recordingVisibility: {
                  showOwnRecordings: true,
                  showTenantRecordings: true,
                  showPublicRecordings: true,
                },
                recordingManagement: {
                  canDeleteOwnRecordings: true,
                  canExportOwnRecordings: false,
                  canShareRecordings: true,
                  canMakeRecordingsPublic: false,
                },
                recordingSettings: {
                  quality: 'medium',
                  autoSave: false,
                  autoName: true,
                }
              }}
              // Deprecated recording props for backward compatibility
              showRecordingControls={externalProps?.uiConfig?.showRecordingControls ?? true}
              allowRecording={externalProps?.uiConfig?.allowRecording ?? true}
              showRecordingsList={externalProps?.uiConfig?.showRecordingsList ?? true}
              allowSolutionViewing={externalProps?.uiConfig?.allowSolutionViewing}
              solutionVisibilityMode={externalProps?.uiConfig?.solutionVisibilityMode}
              allowOwnSolutionManagement={externalProps?.uiConfig?.allowOwnSolutionManagement}
              maxSolutionsToShow={externalProps?.uiConfig?.maxSolutionsToShow}
              showSolutionAuthorInfo={externalProps?.uiConfig?.showSolutionAuthorInfo}
              showSolutionStats={externalProps?.uiConfig?.showSolutionStats}
              currentTestId={qbTestId || undefined}
              showEliminationDisplay={showEliminationDisplay}
              onToggleEliminationDisplay={handleToggleEliminationDisplay}
              hasEliminatedChoices={!!eliminatedChoices[currentQbQuestion ? getStandardQuestionId(currentQbQuestion) || '' : '']}
              testTitle={testTitle}
              isTestMode={timerConfig.isTimeTest || timerConfig.isTimedQuestion}
              totalTime={totalTime}
              isTestCompleted={isTestCompleted}
              onTestComplete={handleTestComplete}
            />
          ) : (
            <ModernToolbar
              hasVideo={(() => {
                if (!currentQbQuestion?.href) return false;
                const href = currentQbQuestion.href as string | Array<{ videoUrl?: string; audioUrl?: string; pdfUrl?: string; title?: string }>;
                if (typeof href === 'string') return true;
                if (Array.isArray(href)) {
                  return href.some(item => !!item.videoUrl);
                }
                return false;
              })()}
              onVideoClick={handleVideoClick}
              allowExport={externalProps?.uiConfig?.allowExport ?? false}
              // Recording permissions
              recordingPermissions={externalProps?.uiConfig?.recordingPermissions || {
                canRecord: true,
                canViewRecordings: true,
                recordingVisibility: {
                  showOwnRecordings: true,
                  showTenantRecordings: true,
                  showPublicRecordings: true,
                },
                recordingManagement: {
                  canDeleteOwnRecordings: true,
                  canExportOwnRecordings: false,
                  canShareRecordings: true,
                  canMakeRecordingsPublic: false,
                },
                recordingSettings: {
                  quality: 'medium',
                  autoSave: false,
                  autoName: true,
                }
              }}
              // Deprecated recording props for backward compatibility
              showRecordingControls={externalProps?.uiConfig?.showRecordingControls ?? true}
              allowRecording={externalProps?.uiConfig?.allowRecording ?? true}
              showRecordingsList={externalProps?.uiConfig?.showRecordingsList ?? true}
              maxRecordingDuration={externalProps?.uiConfig?.maxRecordingDuration}
              recordingQuality={externalProps?.uiConfig?.recordingQuality}
              autoSaveRecording={externalProps?.uiConfig?.autoSaveRecording}
              currentQuestionId={currentQbQuestion ? (getStandardQuestionId(currentQbQuestion) || undefined) : undefined}
              currentTestId={qbTestId || undefined}
              allowSolutionViewing={externalProps?.uiConfig?.allowSolutionViewing}
              solutionVisibilityMode={externalProps?.uiConfig?.solutionVisibilityMode}
              allowOwnSolutionManagement={externalProps?.uiConfig?.allowOwnSolutionManagement}
              maxSolutionsToShow={externalProps?.uiConfig?.maxSolutionsToShow}
              showSolutionAuthorInfo={externalProps?.uiConfig?.showSolutionAuthorInfo}
              // Tablet navigation props
              isTablet={isTablet}
              showTabletQuestionNav={showTabletQuestionNav}
              onToggleTabletQuestionNav={() => setShowTabletQuestionNav(!showTabletQuestionNav)}
              hasQuestions={qbQuestions.length > 0}
              currentQuestionIndex={currentQuestionIndex}
              totalQuestions={qbQuestions.length}
              showSolutionStats={externalProps?.uiConfig?.showSolutionStats}
              showEliminationDisplay={showEliminationDisplay}
              onToggleEliminationDisplay={handleToggleEliminationDisplay}
              hasEliminatedChoices={!!eliminatedChoices[currentQbQuestion ? getStandardQuestionId(currentQbQuestion) || '' : '']}
              // Additional metadata for recording naming
              testTitle={testTitle}
              qbGroups={qbGroups}
              currentQbQuestion={currentQbQuestion}
              // Optical mode and question navigation
              isOpticalMode={isOpticalMode}
              questions={qbQuestions}
              onQuestionNavigate={navigateToQuestion}
            />
          )}
        </>
      )}
      
      {/* Main canvas area */}
      <div className="canvas-area flex-1 relative z-10">
        <WhiteboardCanvas
          ref={whiteboardCanvasRef}
          isDrawingModeEnabled={!isPlayback && currentTool !== 'select'}
          currentTool={currentTool}
          currentColor={currentColor}
          currentStrokeWidth={currentWidth}
          isMobile={isMobile}
          isWebViewMode={isWebViewMode}
          canvasReady={canvasReady}
          onCanvasReady={handleCanvasInit}
          isOpticalMode={isOpticalMode}
          currentQuestionId={currentQbQuestion ? (getStandardQuestionId(currentQbQuestion) || undefined) : undefined}
          currentQuestion={currentQbQuestion}
          eliminatedChoices={eliminatedChoicesForCanvas}
          opticalAnswers={opticalAnswers}
          onOpticalAnswerSelect={(choice: string, questionId: string) => {
           
            handleOpticalAnswerSelect(choice as 'A' | 'B' | 'C' | 'D' | 'E');
          }}
          onOpticalChoiceEliminate={(choice: string, questionId: string) => {
           
            handleOpticalChoiceEliminate(choice);
          }}
          // Add the missing callbacks for optical choice detection
          onOpticalChoiceCircled={(choiceIndex: number) => {
            const choice = String.fromCharCode(65 + choiceIndex) as 'A' | 'B' | 'C' | 'D' | 'E';
           
            handleOpticalAnswerSelect(choice);
          }}
          onOpticalChoiceEliminated={(choiceIndex: number) => {
            const choice = String.fromCharCode(65 + choiceIndex);
           
            handleOpticalChoiceEliminate(choice);
          }}
          showAnswerFeedback={false}
        />
        
        {/* Optical answer panel for desktop */}
        {!isMobile && qbQuestions.length > 0 && isOpticalMode && showOpticalPanelState && (
          <OpticalAnswerPanel
            questions={qbQuestions}
            currentQuestionIndex={currentQuestionIndex}
            opticalAnswers={opticalAnswers}
            eliminatedChoices={eliminatedChoices}
            onAnswerSelect={handleOpticalAnswerSelect}
            onChoiceEliminate={handleOpticalChoiceEliminate}
            onQuestionNavigate={navigateToQuestion}
            onClose={async () => {
             
              await savePendingOpticalAnswers();
              setShowOpticalPanelState(false);
            }}
            qbGroups={qbGroups}
            onTestComplete={handleTestComplete}
            showCompleteButton={true}
          />
        )}
        
        {/* Optical Panel Toggle Button */}
        {!isMobile && qbQuestions.length > 0 && isOpticalMode && (
          <OpticalPanelToggleButton
            isOpen={showOpticalPanelState}
            onClick={() => setShowOpticalPanelState(!showOpticalPanelState)}
          />
        )}
      </div>
      
      
      
      {/* Video Modal */}
      {isVideoModalOpen && (
        <VideoModal
          videoUrl={currentVideoUrl}
          isOpen={isVideoModalOpen}
          onClose={() => {
            setIsVideoModalOpen(false);
            setCurrentVideoUrl('');
          }}
          isMobile={isMobile}
        />
      )}
      
      {/* Exit Confirm Modal */}
      <ExitConfirmModal
        isOpen={isExitModalOpen}
        onClose={() => {
          // Abort any pending exit operation
          if (exitAbortControllerRef.current) {
            exitAbortControllerRef.current.abort();
            exitAbortControllerRef.current = null;
          }
          
          // Clean up any pending exit operations
          if (exitTimeoutRef.current) {
            clearTimeout(exitTimeoutRef.current);
            exitTimeoutRef.current = null;
          }
          if (exitCleanupRef.current) {
            exitCleanupRef.current();
            exitCleanupRef.current = null;
          }
          
          // Reset exit state if in WebView mode
          if (externalProps?.webViewMode && exitState === ExitState.PENDING) {
            setExitState(ExitState.NONE);
          }
          
          setIsExitModalOpen(false);
        }}
        onConfirm={handleExitConfirm}
        isMobile={isMobile}
      />
      
      {/* Solution Selector */}
      {isSolutionSelectorOpen && (
        <SolutionSelector
          solutions={currentSolutions}
          isOpen={isSolutionSelectorOpen}
          onClose={() => {
            setIsSolutionSelectorOpen(false);
            setCurrentSolutions([]);
          }}
          onSelectSolution={(solution) => {
            const videoUrl = typeof solution === 'string' ? solution : solution.videoUrl;
            if (videoUrl) {
              setCurrentVideoUrl(videoUrl);
              setIsVideoModalOpen(true);
              setIsSolutionSelectorOpen(false);
            }
          }}
        />
      )}
      
      {/* Desktop Question Navigation - Hide on tablets */}
      {!isMobile && !isTablet && qbQuestions.length > 0 && (
        <DraggableQuestionNav
          questions={qbQuestions}
          currentIndex={currentQuestionIndex}
          onQuestionSelect={navigateToQuestion}
          onNext={goToNextQuestion}
          onPrevious={goToPreviousQuestion}
          hasNext={currentQuestionIndex < qbQuestions.length - 1}
          hasPrevious={currentQuestionIndex > 0}
        />
      )}
      
      {/* Tablet Question Navigation - Show/Hide based on toolbar toggle */}
      {isTablet && showTabletQuestionNav && qbQuestions.length > 0 && (
        <TabletQuestionNav
          questions={qbQuestions}
          currentIndex={currentQuestionIndex}
          onQuestionSelect={navigateToQuestion}
          onClose={() => setShowTabletQuestionNav(false)}
          timedQuestionMode={false}
          completedQuestions={new Set()}
        />
      )}
      
      {/* Elimination Display for optical mode */}
      {isOpticalMode && (
        <EliminatedChoicesDisplay
          eliminatedChoices={eliminatedChoicesString}
          currentQuestionId={currentQbQuestion ? (getStandardQuestionId(currentQbQuestion) || undefined) : undefined}
          isOpen={showEliminationDisplay}
          onToggle={handleToggleEliminationDisplay}
        />
      )}

      {/* Interval Tracking Debug - WebView only */}
      <IntervalTrackingDebug />
    </div>
  );
};

// Main container component with provider
export const WhiteboardContainer = forwardRef<any, WhiteboardContainerProps>(({
  externalProps,
  isMobile = false,
  isPlayback = false,
  showToolbar = true
}, ref) => {
  return (
    <ErrorBoundary>
      <RouteGuard>
        <WhiteboardProvider externalProps={externalProps}>
          <WhiteboardContent
            isMobile={isMobile}
            isPlayback={isPlayback}
            showToolbar={showToolbar}
            externalProps={externalProps}
          />
        </WhiteboardProvider>
      </RouteGuard>
    </ErrorBoundary>
  );
});

WhiteboardContainer.displayName = 'WhiteboardContainer';